import React, { useState } from "react";
import { Modal, ModalBody, ModalFooter } from "reactstrap";
import { Button } from "components/ui";
import { useMutation } from "@apollo/client";
import {
  LEARNER_BIOMETRICS_DISCONNECT_PROVIDER,
  LEARNER_BIOMETRICS_DELETE_HEALTH_DATA,
} from "utility/graphQl/mutation";
import { LearnerBiometricsProvider } from "apps/user/learner/api/types";
import appInfo from "utility/app-info";

import styles from "./styles.module.scss";
import { logError } from "utility/Function";
import { useTranslation } from "lib/i18n/use-translation";

interface ManageDevicesModalProps {
  isOpen: boolean;
  setIsOpen?: (value) => void;
  modalData: {
    type: string;
    item: LearnerBiometricsProvider;
  };
  setModalData?: (value, item?) => void;
}

const ManageDevicesModal = ({
  isOpen,
  setIsOpen,
  modalData,
  setModalData,
}: ManageDevicesModalProps) => {
  const { t } = useTranslation(["settings", "common"]);
  const [removeDevice, { loading }] = useMutation(
    LEARNER_BIOMETRICS_DISCONNECT_PROVIDER
  );
  const [removeHealthData, { loading: removeHealthLoader }] = useMutation(
    LEARNER_BIOMETRICS_DELETE_HEALTH_DATA
  );
  const [errorMessage, setErrorMessage] = useState(null);

  const deleteDataText = t("settings:manageDevices.deleteConfirmBody");

  const onConfirm = (data) => {
    const { type, item } = data;
    if (type === "remove-device") {
      removeDevice({
        variables: {
          id: item.id,
          appInfo,
        },
      })
        .then((res) => {
          handleClose();
        })
        .catch((error) => {
          setErrorMessage("Something went wrong, please try again.");
          logError("Error while removing device", error);
        });
    } else {
      removeHealthData({
        variables: {
          appInfo,
        },
      })
        .then((res) => {
          handleClose();
        })
        .catch((error) => {
          setErrorMessage("Something went wrong, please try again.");
          logError("Error while removing health data", error);
        });
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setModalData(null);
    setErrorMessage(null);
  };

  return (
    <Modal
      className={styles["manage-devices-modal"]}
      contentClassName={styles["manage-devices-modal-content"]}
      isOpen={isOpen}
      toggle={handleClose}
      style={{ width: "auto", maxWidth: "350px" }}
    >
      <ModalBody>
        <div className={styles["content"]}>
          <p className={styles["title"]}>
            {t("settings:manageDevices.removeConfirmTitle")}
          </p>
          <p className={styles["subtitle"]}>
            {modalData?.type === "remove-device"
              ? t("settings:manageDevices.removeConfirmBody", {
                  device: modalData?.item?.name,
                })
              : deleteDataText}
          </p>
        </div>
      </ModalBody>
      <div className={styles["error-div"]}>
        {errorMessage && (
          <p className={styles["error-message"]}>{errorMessage}</p>
        )}
      </div>
      <ModalFooter className={styles["footer"]}>
        <Button
          variant="primary"
          loading={loading || removeHealthLoader}
          onClick={() => onConfirm(modalData)}
          className={styles["confirm-button"]}
        >
          {t("settings:manageDevices.removeConfirm")}
        </Button>
        <Button
          variant="secondary"
          onClick={handleClose}
          className={styles["cancel-button"]}
        >
          {t("common:action.cancel")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ManageDevicesModal;
